// RacePage.js — /race: famous Premier League races (one click plays them)
// and a build-your-own race chart: any season or span, any measure, up to
// ten clubs, match day by match day. State lives in the URL
// (?from=2011&to=2011&metric=pos&clubs=manchester-city,manchester-united),
// so any view can be shared. Computed in the browser from ?matches=1.

import React, { useMemo, useState } from "react";
import { Autocomplete, Box, Chip, Skeleton, TextField, Typography } from "@mui/material";
import SEO from "./components/SEO";
import AdSlot from "./components/AdSlot";
import { CtaRow, H2, InternalLink, P, PageHeader, ProsePage } from "./components/Prose";
import { BRAND } from "./theme";
import { useSeededQuery } from "./components/scorelines/data";
import RaceChart, { LINE_COLORS } from "./components/clubs/RaceChart";
import { seasonPath } from "./components/clubs/seasons";
import { SeasonBar, darkField, rangeLabelOf, useLeagueParams } from "./components/clubs/LeagueControls";
import { MATCHES_QUERY, METRICS, parseMatches, raceFor, tableFor } from "./components/clubs/league";
import { clubInfo, seasonText, slugify } from "./components/clubs/data";

const SITE_URL = "https://premierleaguescorigami.com";
const MAX_CLUBS = 10;

// Title races, with captions checked against the final tables.
const FAMOUS = [
  { season: 1995, title: "United reel in Newcastle", note: "United 82, Newcastle 78", clubs: ["manchester-united", "newcastle-united"] },
  { season: 1997, title: "Arsenal overtake United", note: "Arsenal 78, United 77", clubs: ["arsenal", "manchester-united"] },
  { season: 1998, title: "United by a point", note: "United 79, Arsenal 78", clubs: ["manchester-united", "arsenal", "chelsea"] },
  { season: 2003, title: "Arsenal's Invincibles", note: "90 points, unbeaten", clubs: ["arsenal", "chelsea", "manchester-united"] },
  { season: 2007, title: "United hold off Chelsea", note: "United 87, Chelsea 85", clubs: ["manchester-united", "chelsea", "arsenal"] },
  { season: 2009, title: "Chelsea by a point", note: "Chelsea 86, United 85", clubs: ["chelsea", "manchester-united", "arsenal"] },
  { season: 2011, title: "City on goal difference", note: "City 89, United 89", clubs: ["manchester-city", "manchester-united"] },
  { season: 2013, title: "City catch Liverpool", note: "City 86, Liverpool 84", clubs: ["manchester-city", "liverpool", "chelsea"] },
  { season: 2015, title: "Leicester's 5,000–1 title", note: "Leicester 81, Arsenal 71", clubs: ["leicester-city", "arsenal", "tottenham-hotspur", "manchester-city"] },
  { season: 2018, title: "City pip Liverpool", note: "City 98, Liverpool 97", clubs: ["manchester-city", "liverpool"] },
  { season: 2021, title: "City by a point again", note: "City 93, Liverpool 92", clubs: ["manchester-city", "liverpool"] },
  { season: 2022, title: "City chase down Arsenal", note: "City 89, Arsenal 84", clubs: ["manchester-city", "arsenal"] },
  { season: 2023, title: "City edge Arsenal", note: "City 91, Arsenal 89", clubs: ["manchester-city", "arsenal", "liverpool"] },
];

export default function RacePage() {
  const resp = useSeededQuery(MATCHES_QUERY);
  const data = useMemo(() => (resp.data ? parseMatches(resp.data) : null), [resp.data]);
  return (
    <ProsePage wide>
      <SEO
        title="Premier League title race chart"
        description="Watch Premier League title races unfold match by match, from 1995/96 to 2023/24, or chart any clubs and seasons since 1992. Download any race as a video."
        path="/race"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
            { "@type": "ListItem", position: 2, name: "Race chart", item: `${SITE_URL}/race` },
          ],
        }}
      />
      <Box sx={{ maxWidth: 820 }}>
        <PageHeader
          eyebrow="Race chart"
          title="Premier League title race chart"
          lede="Watch any season, or all of them, play out match day by match day. Pick a famous title race, or choose the seasons, the measure and up to ten clubs yourself."
        />
      </Box>
      {resp.error ? (
        <P>The race chart couldn't be loaded right now. Please try again shortly.</P>
      ) : !data ? (
        <>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 1.25, mb: 3 }}>
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} variant="rounded" height={74} sx={{ bgcolor: "rgba(255,255,255,0.06)", borderRadius: 2 }} />
            ))}
          </Box>
          <Skeleton variant="rounded" height={420} sx={{ bgcolor: "rgba(255,255,255,0.06)", borderRadius: 3 }} />
        </>
      ) : (
        <RaceView data={data} />
      )}
      <CtaRow
        links={[
          { to: "/all-time-table", label: "All-time table", primary: true },
          { to: "/clubs", label: "Find your club" },
          { to: "/", label: "Explore the grid" },
        ]}
      />
    </ProsePage>
  );
}

function RaceView({ data }) {
  const lp = useLeagueParams(data);
  const { params, first, last, from, to, update } = lp;
  const [status, setStatus] = useState("all");
  const [autoplay, setAutoplay] = useState(null); // { n, viewKey } of a view to play

  const metric = METRICS.find((m) => m.key === params.get("metric")) || METRICS.find((m) => m.key === "pts");
  const table = useMemo(() => tableFor(data, from, to), [data, from, to]);
  const race = useMemo(() => raceFor(data, from, to), [data, from, to]);
  const isCurrent = (t) => t.lastSeasonOverall === data.currentSeason;
  const byStatus = (t) => (status === "all" ? true : status === "current" ? isCurrent(t) : !isCurrent(t));
  const counts = { all: table.length, current: table.filter(isCurrent).length, former: table.filter((t) => !isCurrent(t)).length };

  // Clubs: from the URL if they played in the range, else the top six.
  const slugToIdx = useMemo(() => new Map(data.teams.map((t, i) => [clubInfo(t.team).slug, i])), [data]);
  const inRange = new Set(table.map((t) => t.idx));
  const cleared = params.get("clubs") === "none";
  const fromUrl = (params.get("clubs") || "")
    .split(",")
    .map((slug) => slugToIdx.get(slug))
    .filter((i) => i !== undefined && inRange.has(i))
    .slice(0, MAX_CLUBS);
  const selected = cleared ? [] : fromUrl.length ? fromUrl : table.slice(0, 6).map((t) => t.idx);
  const slugs = (idxs) => idxs.map((i) => clubInfo(data.teams[i].team).slug).join(",");
  // An empty pick stays empty ("none") rather than falling back to the top six.
  const setSelected = (idxs) => update({ clubs: idxs.length ? slugs(idxs) : "none" });

  const play = (changes) => {
    const viewKey = update(changes);
    setAutoplay((a) => ({ n: (a?.n || 0) + 1, viewKey }));
    // The chart sits below the cards; bring it into view so the start isn't missed.
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    document.getElementById("race")?.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
  };
  const playFamous = (f) => play({ from: f.season, to: f.season, metric: null, clubs: f.clubs.join(",") });
  const playAllTime = () =>
    play({ from: null, to: null, metric: null, clubs: slugs(tableFor(data, first, last).slice(0, 6).map((t) => t.idx)) });
  const activeFamous = FAMOUS.find((f) => f.season === from && f.season === to && f.clubs.join(",") === params.get("clubs"));
  const rangeLabel = rangeLabelOf(from, to, first, last);
  // What the downloadable video is called: the famous race, the clubs (up to
  // three), or the range.
  const video = {
    range: rangeLabel,
    title: activeFamous
      ? activeFamous.title
      : selected.length && selected.length <= 3
        ? selected.map((i) => clubInfo(data.teams[i].team).short).join(" v ")
        : rangeLabel === "All-time"
          ? "The all-time race"
          : `The ${rangeLabel} race`,
    fileName: slugify(["premier league race", rangeLabel, activeFamous?.title, metric.key !== "pts" && metric.label].filter(Boolean).join(" ").replace(/['’]/g, "")),
  };

  return (
    <>
      <H2 id="famous">Famous races</H2>
      <Box
        component="ul"
        aria-label="Famous races"
        sx={{ listStyle: "none", p: 0, m: 0, mb: 4, display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(auto-fill, minmax(190px, 1fr))" }, gap: 1.25 }}
      >
        {FAMOUS.map((f) => (
          <li key={f.season}>
            <RaceCard active={activeFamous === f} season={seasonText(f.season)} title={f.title} note={f.note} onClick={() => playFamous(f)} />
          </li>
        ))}
        <li>
          <RaceCard active={false} season="All-time" title="The all-time top six" note="Every season since 1992/93" onClick={playAllTime} />
        </li>
      </Box>

      <H2 id="race">{activeFamous ? `${seasonText(activeFamous.season)}: ${activeFamous.title}` : `${rangeLabel} race`}</H2>
      {from === to && (
        <Typography sx={{ mt: -0.5, mb: 1.5, fontSize: 14.5 }}>
          <InternalLink to={seasonPath(from)}>The {seasonText(from)} season: final table, results and records →</InternalLink>
        </Typography>
      )}
      <SeasonBar data={data} lp={lp} status={status} onStatus={setStatus} counts={counts} />
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", gap: 1.5, mt: 1.5, mb: 2 }}>
        <TextField
          id="race-metric"
          select
          size="small"
          label="Measure"
          value={metric.key}
          onChange={(e) => update({ metric: e.target.value === "pts" ? null : e.target.value })}
          SelectProps={{ native: true }}
          sx={darkField({ width: 190 })}
        >
          {METRICS.map((m) => (
            <option key={m.key} value={m.key}>
              {m.label}
            </option>
          ))}
        </TextField>
        <Autocomplete
          multiple
          id="race-clubs"
          options={table.filter(byStatus).map((t) => t.idx)}
          value={selected}
          onChange={(_, v) => setSelected(v.slice(0, MAX_CLUBS))}
          getOptionLabel={(i) => clubInfo(data.teams[i].team).name}
          getOptionDisabled={(i) => selected.length >= MAX_CLUBS && !selected.includes(i)}
          disableCloseOnSelect
          filterSelectedOptions
          renderOption={(props, i) => {
            const { key, ...rest } = props;
            const t = data.teams[i];
            return (
              <li key={key} {...rest}>
                {t.image && <Box component="img" src={t.image} alt="" sx={{ width: 20, height: 20, objectFit: "contain", mr: 1.25 }} />}
                {clubInfo(t.team).name}
              </li>
            );
          }}
          renderTags={(value, getTagProps) =>
            value.map((i, k) => {
              const { key, ...tagProps } = getTagProps({ index: k });
              return (
                <Chip
                  key={key}
                  {...tagProps}
                  size="small"
                  label={clubInfo(data.teams[i].team).short}
                  sx={{ fontWeight: 700, color: "#fff", background: "rgba(255,255,255,0.06)", borderLeft: `3px solid ${LINE_COLORS[k]}`, borderRadius: 1 }}
                />
              );
            })
          }
          renderInput={(p) => <TextField {...p} size="small" label={`Clubs (up to ${MAX_CLUBS})`} placeholder={selected.length ? "" : "Pick clubs"} />}
          sx={darkField({ flex: 1, minWidth: { xs: "100%", sm: 320 } })}
        />
        <Box sx={{ display: "flex", gap: 1 }}>
          {[
            ["Top 6", () => setSelected(table.filter(byStatus).slice(0, 6).map((t) => t.idx))],
            ["Clear", () => update({ clubs: "none" })],
          ].map(([label, onClick]) => (
            <Chip
              key={label}
              label={label}
              clickable
              onClick={onClick}
              sx={{ fontWeight: 700, color: "#e6e8ee", border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.04)", height: 40, borderRadius: 999 }}
            />
          ))}
        </Box>
      </Box>

      {cleared || !selected.length ? (
        <P>Pick up to {MAX_CLUBS} clubs to compare.</P>
      ) : race.days.length > 0 ? (
        <RaceChart race={race} teams={data.teams} selected={selected} colors={LINE_COLORS} metric={metric} autoplay={autoplay} viewKey={params.toString()} video={video} />
      ) : null}

      <Typography sx={{ color: BRAND.textSecondary, fontSize: 13, lineHeight: 1.7, mt: 3, maxWidth: 820 }}>
        Points are three for a win and one for a draw, minus the Premier League's point deductions, each applied on the
        last match day of its season. The chart moves one match day at a time; days without a Premier League match are
        left out.
      </Typography>
      <Box sx={{ mt: 3 }}>
        <AdSlot position="records" />
      </Box>
    </>
  );
}

function RaceCard({ season, title, note, onClick, active }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      aria-pressed={active}
      sx={{
        font: "inherit",
        textAlign: "left",
        cursor: "pointer",
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 0.4,
        px: 1.5,
        py: 1.25,
        borderRadius: 2,
        color: "#fff",
        background: active ? "rgba(111,93,245,0.22)" : "linear-gradient(180deg, rgba(25,25,32,0.85) 0%, rgba(18,18,24,0.85) 100%)",
        border: `1px solid ${active ? BRAND.violet : "rgba(255,255,255,0.08)"}`,
        transition: "border-color 140ms ease-out, transform 140ms ease-out",
        "&:hover, &:focus-visible": { borderColor: BRAND.cyan, transform: "translateY(-1px)", outline: "none" },
      }}
    >
      <Box component="span" sx={{ color: BRAND.cyan, fontSize: 11, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase" }}>
        {season}
      </Box>
      <Box component="span" sx={{ fontSize: 14.5, fontWeight: 700, lineHeight: 1.3 }}>
        {title}
      </Box>
      <Box component="span" sx={{ color: BRAND.textSecondary, fontSize: 12.5 }}>
        {note}
      </Box>
    </Box>
  );
}
